import { Suspense, lazy } from 'react';
import { ClientOnly } from '@/components/ClientOnly';
// Type-only: erased at build time, so it does not pull Leaflet into this chunk.
import type { LeafletMapProps } from './LeafletMap';

/**
 * Leaflet reads `window` and `document` the moment it is imported, so a static
 * import would crash any page that renders on the server (web plan W4.2). The
 * lazy import runs only when the map itself renders, which `ClientOnly` keeps
 * to the browser.
 */
const LeafletMap = lazy(() => import('./LeafletMap'));

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

/** Stands in for the map (server, hydration, chunk loading), filling the slot. */
export function MapPlaceholder() {
  return (
    <div
      aria-hidden="true"
      data-map-placeholder=""
      className="h-full w-full animate-pulse rounded-2xl bg-line-light"
    />
  );
}

/**
 * The Leaflet map, browser-only. The slot takes the caller's size classes
 * (`className`, e.g. `h-72 lg:h-[560px]`) and keeps them while the placeholder
 * shows, so the page does not move when the map arrives.
 */
export function ClientLeafletMap({ className, ...props }: LeafletMapProps) {
  return (
    <div className={cn('relative', className)}>
      <ClientOnly fallback={<MapPlaceholder />}>
        <Suspense fallback={<MapPlaceholder />}>
          <LeafletMap {...props} className="h-full" />
        </Suspense>
      </ClientOnly>
    </div>
  );
}

export default ClientLeafletMap;
